const STORAGE_KEY = "workpermit-chat-messages";
const DEFAULT_RETENTION_MINUTES = 24 * 60;
const retentionMinutesFromEnv = Number(
  process.env.REACT_APP_CHAT_RETENTION_MINUTES
);
const retentionMinutes =
  Number.isFinite(retentionMinutesFromEnv) && retentionMinutesFromEnv > 0
    ? retentionMinutesFromEnv
    : DEFAULT_RETENTION_MINUTES;
const RETENTION_MS = retentionMinutes * 60 * 1000;

const toTimestamp = (value) => {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getCutoffTimestamp = () => Date.now() - RETENTION_MS;

const normalizeConversationIdValue = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
};

const normalizeConversationIdsByUser = (conversationIdsByUser) => {
  if (!conversationIdsByUser || typeof conversationIdsByUser !== "object") {
    return {};
  }

  return Object.entries(conversationIdsByUser).reduce((acc, [userId, value]) => {
    if (userId) {
      const normalized = normalizeConversationIdValue(value);
      acc[String(userId)] = normalized;
    }
    return acc;
  }, {});
};

const readStorageData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { messages: [], conversationIdsByUser: {} };
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { messages: parsed, conversationIdsByUser: {} };
    }

    if (Array.isArray(parsed?.messages)) {
      const conversationIdsByUser = normalizeConversationIdsByUser(
        parsed?.conversationIdsByUser || parsed?.conversationIds || {}
      );
      return {
        messages: parsed.messages,
        conversationIdsByUser,
      };
    }

    return { messages: [], conversationIdsByUser: {} };
  } catch (error) {
    return { messages: [], conversationIdsByUser: {} };
  }
};

const writeStorage = (messages, conversationIdsByUser = {}) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ 
      messages, 
      conversationIdsByUser: normalizeConversationIdsByUser(conversationIdsByUser)
    })
  );
};

const pruneExpiredMessages = (messages) => {
  const cutoffTimestamp = getCutoffTimestamp();
  return messages.filter((message) => {
    const timestamp = toTimestamp(message?.created_at);
    return timestamp !== null && timestamp >= cutoffTimestamp;
  });
};

const normalizeMessage = (message) => ({
  id: message?.id,
  userId: message?.userId,
  created_at: message?.created_at,
  sent: Boolean(message?.sent),
  text: String(message?.text || ""),
});

const cleanupStorageData = () => {
  const { messages: storedMessages, conversationIdsByUser: storedConversationIdsByUser } =
    readStorageData();
  const messages = pruneExpiredMessages(storedMessages);
  
  const activeUserIds = new Set(
    messages
      .map((message) => message?.userId)
      .filter((userId) => userId)
  );
  
  const conversationIdsByUser = {};
  activeUserIds.forEach((userId) => {
    conversationIdsByUser[String(userId)] = normalizeConversationIdValue(
      storedConversationIdsByUser?.[String(userId)]
    );
  });
  
  writeStorage(messages, conversationIdsByUser);
  return { messages, conversationIdsByUser };
};

const ChatStorageService = {
  cleanupExpiredMessages: () => {
    const { messages } = cleanupStorageData();
    return messages;
  },

  getUserMessages: (userId) => {
    const { messages } = cleanupStorageData();

    if (!userId) {
      return [];
    }

    const userIdAsString = String(userId);
    return messages
      .filter((message) => String(message?.userId) === userIdAsString)
      .map((message) => normalizeMessage(message));
  },

  getUserConversationId: (userId) => {
    const { messages, conversationIdsByUser } = cleanupStorageData();

    if (!userId) {
      return null;
    }

    const userIdAsString = String(userId);
    const hasMessagesForCurrentUser = messages.some(
      (message) => String(message?.userId) === userIdAsString
    );

    if (!hasMessagesForCurrentUser) {
      return null;
    }

    return normalizeConversationIdValue(conversationIdsByUser?.[userIdAsString]);
  },

  saveUserConversationId: (userId, conversationId) => {
    if (!userId) {
      return;
    }

    const userIdAsString = String(userId);
    const { messages, conversationIdsByUser } = cleanupStorageData();
    const hasMessagesForCurrentUser = messages.some(
      (message) => String(message?.userId) === userIdAsString
    );
    
    const conversationIdToStore = hasMessagesForCurrentUser
      ? normalizeConversationIdValue(conversationId)
      : null;

    conversationIdsByUser[userIdAsString] = conversationIdToStore;
    writeStorage(messages, conversationIdsByUser);
  },

  saveMessage: ({ id, userId, created_at, sent, text }) => {
    if (!userId || !created_at) {
      return;
    }

    const userIdAsString = String(userId);
    const { messages, conversationIdsByUser } = cleanupStorageData();

    const newMessage = normalizeMessage({
      id,
      userId: userIdAsString,
      created_at,
      sent,
      text,
    });

    messages.push(newMessage);

    if (!(userIdAsString in conversationIdsByUser)) {
      conversationIdsByUser[userIdAsString] = null;
    }

    writeStorage(messages, conversationIdsByUser);
  },
};

export default ChatStorageService;
